import React, { useEffect, useState, type ReactNode } from 'react';
import type { StyleProp, ViewStyle } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { ActivityIndicator, Image, Modal, StyleSheet, Text, TouchableOpacity, View } from './rn';
import GlassControlButton from './GlassControlButton';
import PageHeader from './PageHeader';
import ProfileSection from './ProfileSection';
import ZoomablePhoto from './ZoomablePhoto';
import { formatVideoDuration, NativeVideoView } from './MessageVideo';
import { ApiError, mediaUrl, type ProfileMediaItem } from '../services/api';
import { useTheme } from '../theme/ThemeContext';
import { useTranslation } from '../i18n/I18nContext';
import { formatDisplayCount } from '../i18n/formatCount';
import { confirmDestructive } from '../utils/confirmDestructive';

// 9.35.3 · D-830 — photos and videos on a profile.
//
// A standard three-column grid, as Apple's collections guidance asks: square
// tiles, a 2 pt gap, tap to open. A video tile is a dark square with a play
// symbol and its length. It does not load a frame: up to 60 tiles would each
// open a player and spend data nobody asked for. The viewer plays a video with
// the system controls at its own aspect ratio, and zooms a photo like the
// profile picture viewer.
//
// 9.35.4 — on one's own profile an item can be removed from the viewer's trash
// button or by touching and holding its tile. Removal deletes the file and
// cannot be undone, so it is confirmed first (Apple's alert guidance).

const COLUMNS = 3;
const GAP = 2;
const LINK = '#2878d4';
/** The server's limit (api/src/profile-media.ts); a test keeps them equal. 1000 since 16 October 2026. */
export const PROFILE_MEDIA_LIMIT = 1000;

/** One page from the server; `next_cursor` and `total` are absent on an older API. */
export type ProfileMediaPage = { items: ProfileMediaItem[]; next_cursor?: string | null; total?: number };

type Props = {
  /** Loads one page: the first with `null`, the next with the cursor the last page gave. */
  load: (cursor: string | null) => Promise<ProfileMediaPage>;
  /** A new value reloads the list, e.g. another profile or a pull to refresh. */
  reloadKey: unknown;
  ownerName: string;
  testID: string;
  /** Own profile: the grid sits in a titled card like the other sections. */
  header?: ReactNode;
  /** Spacing around the bare grid; ignored inside a titled card. */
  style?: StyleProp<ViewStyle>;
  /** Own profile only: removes one item on the server. */
  onRemove?: (item: ProfileMediaItem) => Promise<void>;
  /** Own profile only: the most items a profile can hold, shown as "7/60". */
  limit?: number;
  /** Own profile only (9.18.10.4): opens "Import from Instagram"; shown even while empty. */
  onAdd?: () => void;
};

type State =
  | { status: 'loading' | 'error' }
  | { status: 'ready'; items: ProfileMediaItem[]; nextCursor: string | null; total: number };

function readyFrom(page: ProfileMediaPage, before: ProfileMediaItem[] = []): Extract<State, { status: 'ready' }> {
  const fresh = Array.isArray(page?.items) ? page.items : [];
  // A page may overlap the last one if items were added meanwhile; keep each once.
  const seen = new Set(before.map((item) => item.id));
  const items = [...before, ...fresh.filter((item) => !seen.has(item.id))];
  const nextCursor = typeof page?.next_cursor === 'string' ? page.next_cursor : null;
  return { status: 'ready', items, nextCursor, total: typeof page?.total === 'number' ? page.total : items.length };
}

export function isVideo(item: Pick<ProfileMediaItem, 'mime_type'>): boolean {
  return item.mime_type.startsWith('video/');
}

export default function ProfileMediaSection({ load, reloadKey, ownerName, testID, header, style, onRemove, limit, onAdd }: Props) {
  const { t, lang } = useTranslation();
  const colors = useTheme();
  const [state, setState] = useState<State>({ status: 'loading' });
  const [attempt, setAttempt] = useState(0);
  const [open, setOpen] = useState<number | null>(null);
  const [width, setWidth] = useState(0);
  const [removeFailed, setRemoveFailed] = useState<string | null>(null);
  const [more, setMore] = useState<'idle' | 'loading' | 'error'>('idle');

  useEffect(() => {
    let live = true;
    setState({ status: 'loading' });
    setMore('idle');
    load(null)
      .then((page) => { if (live) setState(readyFrom(page)); })
      .catch((error) => {
        if (!live) return;
        // 404 means there is nothing to show here: an account waiting for
        // deletion, a page closed to people without an account, or a server
        // that does not have this route yet. None of those is a failure.
        setState(error instanceof ApiError && error.status === 404 ? readyFrom({ items: [] }) : { status: 'error' });
      });
    return () => { live = false; };
    // `load` is a new function on every render of the screen; the key decides.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadKey, attempt]);

  // The next page is asked for only when the person wants it, never on its own.
  const loadMore = () => {
    if (state.status !== 'ready' || !state.nextCursor || more === 'loading') return;
    const { items: before, nextCursor } = state;
    setMore('loading');
    load(nextCursor)
      .then((page) => { setState(readyFrom(page, before)); setMore('idle'); })
      .catch(() => setMore('error'));
  };

  // Nothing to show is not news on someone else's profile. On one's own, an
  // empty section still offers the way to fill it.
  if (state.status === 'loading') return null;
  if (state.status === 'ready' && state.items.length === 0) {
    if (!onAdd || !header) return null;
    return <ProfileSection header={header} testID={`${testID}-section`}>
      <TouchableOpacity testID={`${testID}-add-empty`} accessibilityRole="button" onPress={onAdd} style={styles.addRow}>
        <Ionicons name="logo-instagram" size={20} color={colors.black} accessible={false} />
        <Text style={[styles.addRowText, { color: colors.black }]}>{t('instagramImport.addEntry')}</Text>
        <Text style={[styles.addRowChevron, { color: colors.gray600 }]}>›</Text>
      </TouchableOpacity>
    </ProfileSection>;
  }

  const items = state.status === 'ready' ? state.items : [];
  const total = state.status === 'ready' ? state.total : 0;
  const remove = onRemove ? (item: ProfileMediaItem) => confirmDestructive({
    title: t(isVideo(item) ? 'profile.mediaRemoveVideoTitle' : 'profile.mediaRemovePhotoTitle'),
    message: t('profile.mediaRemoveBody'),
    confirm: t('profile.mediaRemove'),
    cancel: t('common.cancel'),
  }, () => {
    setRemoveFailed(null);
    onRemove(item)
      .then(() => {
        setOpen(null);
        setState((current) => current.status === 'ready'
          ? { ...current, items: current.items.filter((other) => other.id !== item.id), total: Math.max(0, current.total - 1) }
          : current);
      })
      .catch(() => setRemoveFailed(item.id));
  }) : null;
  const removeError = <Text testID={`${testID}-remove-error`} style={[styles.removeError, { color: colors.black }]}>
    {t('profile.mediaRemoveFailed')}</Text>;
  const body = state.status === 'error'
    ? (
      <View style={[styles.errorRow, !header && style]} testID={`${testID}-error`}>
        <Text style={[styles.errorText, { color: colors.gray600 }]}>{t('profile.mediaUnavailable')}</Text>
        <TouchableOpacity testID={`${testID}-retry`} accessibilityRole="button" style={styles.retry}
          onPress={() => setAttempt((value) => value + 1)}>
          <Text style={[styles.retryText, { color: colors.black }]}>{t('common.retry')}</Text>
        </TouchableOpacity>
      </View>
    )
    : (
      <View testID={testID} style={[styles.grid, !header && style]} onLayout={(event) => setWidth(event.nativeEvent.layout.width)}
        accessibilityLabel={t('profile.photos')}>
        {width > 0 && items.map((item, index) => {
          // Whole points keep rows from wrapping on rounding; the last column
          // takes the remainder so the grid reaches both edges.
          const size = Math.floor((width - GAP * (COLUMNS - 1)) / COLUMNS);
          const lastColumn = index % COLUMNS === COLUMNS - 1;
          const tileWidth = lastColumn ? width - (COLUMNS - 1) * (size + GAP) : size;
          const video = isVideo(item);
          const duration = video ? formatVideoDuration(item.duration_seconds) : null;
          const label = t(video ? 'profile.mediaVideoLabel' : 'profile.mediaPhotoLabel',
            { n: formatDisplayCount(index + 1, lang), total: formatDisplayCount(total, lang) })
            + (duration ? `, ${duration}` : '');
          return (
            <TouchableOpacity key={item.id} testID={`${testID}-tile-${index}`} accessibilityRole="button"
              accessibilityLabel={label} activeOpacity={0.85} onPress={() => setOpen(index)}
              // Touch and hold edits, as in Photos; screen readers get the same action by name.
              onLongPress={remove ? () => remove(item) : undefined}
              accessibilityActions={remove ? [{ name: 'delete', label: t('profile.mediaRemove') }] : undefined}
              onAccessibilityAction={remove ? (event) => { if (event.nativeEvent.actionName === 'delete') remove(item); } : undefined}
              style={{ width: tileWidth, height: size, marginRight: lastColumn ? 0 : GAP, marginBottom: GAP }}>
              {video ? (
                <View style={[styles.fill, styles.videoTile]} testID={`${testID}-video-${index}`}>
                  <Ionicons name="play" size={28} color="#fff" accessible={false} />
                  {duration ? <Text style={styles.duration}>{duration}</Text> : null}
                </View>
              ) : (
                <Image source={{ uri: mediaUrl(item.url) }} style={[styles.fill, { backgroundColor: colors.gray100 }]}
                  resizeMode="cover" accessible={false} testID={`${testID}-image-${index}`} />
              )}
            </TouchableOpacity>
          );
        })}
      </View>
    );

  const nextCursor = state.status === 'ready' ? state.nextCursor : null;
  const content = <>
    {removeFailed !== null && open === null ? removeError : null}
    {body}
    {nextCursor ? (
      <TouchableOpacity testID={`${testID}-more`} accessibilityRole="button" disabled={more === 'loading'}
        accessibilityState={{ busy: more === 'loading' }} onPress={loadMore} style={styles.moreButton}>
        {more === 'loading'
          ? <ActivityIndicator accessibilityLabel={t('common.loading')} />
          : <Text style={[styles.moreText, { color: LINK }]}>{more === 'error' ? t('common.retry') : t('profile.mediaMore')}</Text>}
      </TouchableOpacity>
    ) : null}
  </>;
  const titled = header && typeof limit === 'number'
    ? <View style={styles.headerRow}>
        {header}
        <View style={styles.headerEnd}>
          <Text testID={`${testID}-count`} style={[styles.count, { color: colors.gray600 }]}>
            {`${formatDisplayCount(total, lang)}/${formatDisplayCount(limit, lang)}`}
          </Text>
          {onAdd && total < limit ? <TouchableOpacity testID={`${testID}-add`} accessibilityRole="button"
            accessibilityLabel={t('instagramImport.addEntry')} onPress={onAdd} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
            <Ionicons name="add-outline" size={18} color="#2878d4" accessible={false} />
          </TouchableOpacity> : null}
        </View>
      </View>
    : header;
  const viewed = open !== null ? items[open] : undefined;
  return <>
    {titled ? <ProfileSection header={titled} testID={`${testID}-section`}>{content}</ProfileSection> : content}
    {viewed && (
      <MediaViewer item={viewed} title={ownerName} testID={`${testID}-viewer`} onClose={() => setOpen(null)}
        onRemove={remove ? () => remove(viewed) : undefined}
        removeError={removeFailed === viewed.id ? removeError : null} />
    )}
  </>;
}

function MediaViewer({ item, title, testID, onClose, onRemove, removeError }: {
  item: ProfileMediaItem; title: string; testID: string; onClose: () => void;
  onRemove?: () => void; removeError: ReactNode;
}) {
  const colors = useTheme();
  const insets = useSafeAreaInsets();
  const { t } = useTranslation();
  return (
    <Modal visible animationType="slide" onRequestClose={onClose}>
      <View testID={testID} style={[styles.page, { backgroundColor: colors.white, paddingBottom: insets.bottom }]}>
        <PageHeader title={title} onBack={onClose} backTestID={`${testID}-close`}
          action={onRemove ? <GlassControlButton testID={`${testID}-remove`} accessibilityLabel={t('profile.mediaRemove')}
            onPress={onRemove}>
            <Ionicons name="trash-outline" size={22} color={colors.black} accessible={false} />
          </GlassControlButton> : undefined} />
        {removeError}
        {isVideo(item) ? <VideoPlayer uri={mediaUrl(item.url)} testID={`${testID}-video`} />
          : <PhotoAttempt uri={mediaUrl(item.url)} testID={`${testID}-image`} />}
      </View>
    </Modal>
  );
}

function VideoPlayer({ uri, testID }: { uri: string; testID: string }) {
  const { t } = useTranslation();
  const colors = useTheme();
  // Without the native module (an older binary on this update channel) there
  // is nothing to play; say what helps instead of drawing a dead control.
  if (!NativeVideoView) {
    return <View style={styles.frame}><Text testID={`${testID}-unavailable`} style={[styles.errorText, { color: colors.black }]}>
      {t('message.videoNeedsUpdate')}</Text></View>;
  }
  return (
    <View style={[styles.frame, styles.videoFrame]}>
      <NativeVideoView testID={testID} source={{ uri }} style={styles.video} resizeMode="contain"
        controls paused={false} repeat={false} />
    </View>
  );
}

function PhotoAttempt({ uri, testID }: { uri: string; testID: string }) {
  const [attempt, setAttempt] = useState(0);
  return <Photo key={attempt} uri={uri} testID={testID} onRetry={() => setAttempt((value) => value + 1)} />;
}

function Photo({ uri, testID, onRetry }: { uri: string; testID: string; onRetry: () => void }) {
  const { t } = useTranslation();
  const colors = useTheme();
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');
  return (
    <View style={styles.frame}>
      {state !== 'error' && <ZoomablePhoto imageTestID={testID} uri={uri} accessibilityLabel={t('profile.photos')}
        onLoad={() => setState((previous) => previous === 'error' ? previous : 'ready')} onError={() => setState('error')} />}
      {state === 'loading' && <Text style={[styles.loading, { color: colors.gray600 }]}>{t('common.loading')}</Text>}
      {state === 'error' && <View style={styles.errorBlock}>
        <Text style={[styles.errorText, { color: colors.black }]}>{t('profile.photoUnavailable')}</Text>
        <TouchableOpacity testID={`${testID}-retry`} accessibilityRole="button" style={styles.retry} onPress={onRetry}>
          <Text style={[styles.retryText, { color: colors.black }]}>{t('common.retry')}</Text>
        </TouchableOpacity>
      </View>}
    </View>
  );
}

const styles = StyleSheet.create({
  grid: { flexDirection: 'row', flexWrap: 'wrap' },
  fill: { width: '100%', height: '100%' },
  videoTile: { backgroundColor: '#1C1C1E', alignItems: 'center', justifyContent: 'center' },
  duration: { position: 'absolute', right: 6, bottom: 4, color: '#fff', fontSize: 12, fontWeight: '600' },
  errorRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 12, paddingHorizontal: 16 },
  errorText: { fontSize: 15, textAlign: 'center', flexShrink: 1 },
  errorBlock: { padding: 24, gap: 16, alignItems: 'center' },
  retry: { minHeight: 44, minWidth: 44, padding: 12, alignItems: 'center', justifyContent: 'center' },
  retryText: { fontSize: 16 },
  page: { flex: 1 },
  frame: { flex: 1, minHeight: 0, alignItems: 'center', justifyContent: 'center' },
  videoFrame: { backgroundColor: '#000' },
  video: { width: '100%', height: '100%' },
  loading: { position: 'absolute', fontSize: 16 },
  headerRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 8 },
  count: { fontSize: 13, fontVariant: ['tabular-nums'] },
  headerEnd: { flexDirection: 'row', alignItems: 'center', gap: 12 },
  moreButton: { minHeight: 44, alignItems: 'center', justifyContent: 'center', marginTop: 8 },
  moreText: { fontSize: 16 },
  addRow: { flexDirection: 'row', alignItems: 'center', gap: 10, minHeight: 48, paddingHorizontal: 4 },
  addRowText: { flex: 1, fontSize: 16 },
  // The app's row chevron (OwnProfileScreen `rowChevron`); a test keeps every '›' alike.
  addRowChevron: { flexShrink: 0, width: 12, textAlign: 'center', fontSize: 16, marginLeft: 8 },
  removeError: { fontSize: 15, paddingHorizontal: 16, paddingVertical: 8 },
});
